import { api } from '@/api/client'
import type { components } from '@/api/types.gen'

export type TreeNode = components['schemas']['TreeNode']
export type Category = components['schemas']['Category']
export type Footprint = components['schemas']['Footprint']
export type StorageLocation = components['schemas']['StorageLocation']
export type AttachmentType = components['schemas']['AttachmentType']

export type TreeKind = 'categories' | 'footprints' | 'locations' | 'attachment-types'

export interface TreeKindInfo {
  /** Singular, lower case. */
  label: string
  /** Plural, lower case. */
  plural: string
}

export const TREE_KINDS: Record<TreeKind, TreeKindInfo> = {
  categories: { label: 'category', plural: 'categories' },
  footprints: { label: 'footprint', plural: 'footprints' },
  locations: { label: 'storage location', plural: 'locations' },
  'attachment-types': { label: 'attachment type', plural: 'attachment types' },
}

export function treeKey(kind: TreeKind) {
  return ['tree', kind] as const
}

export function listKey(kind: TreeKind) {
  return ['tree-list', kind] as const
}

/** The input schema each kind writes through `create` and `update`. */
export type TreeInput<K extends TreeKind = TreeKind> = {
  categories: components['schemas']['CategoryInput']
  footprints: components['schemas']['FootprintInput']
  locations: components['schemas']['StorageLocationInput']
  'attachment-types': components['schemas']['AttachmentTypeInput']
}[K]

/** The detail document `get` returns for each kind. */
export type TreeDetail<K extends TreeKind = TreeKind> = {
  categories: components['schemas']['Detail_Category']
  footprints: components['schemas']['Detail_Footprint']
  locations: components['schemas']['Detail_StorageLocation']
  'attachment-types': components['schemas']['Detail_AttachmentType']
}[K]

/**
 * Per-kind bindings to the generated client. Written out rather than built from
 * a string so every call keeps its request and response types; the annotation
 * keeps `TREE_API[kind].create/update` correlated with `kind` itself.
 */
interface TreeBinding<K extends TreeKind> {
  tree: () => Promise<TreeNode[]>
  get: (id: string) => Promise<TreeDetail<K> | undefined>
  create: (body: TreeInput<K>) => Promise<{ data?: { id: string } }>
  update: (id: string, body: TreeInput<K>) => Promise<unknown>
  remove: (id: string) => Promise<unknown>
  move: (id: string, parent_id: string | null) => Promise<unknown>
}

export const TREE_API: { [K in TreeKind]: TreeBinding<K> } = {
  categories: {
    tree: async () => (await api.GET('/api/categories/tree')).data ?? [],
    get: async (id: string) =>
      (await api.GET('/api/categories/{id}', { params: { path: { id } } })).data,
    create: (body: components['schemas']['CategoryInput']) => api.POST('/api/categories', { body }),
    update: (id: string, body: components['schemas']['CategoryInput']) =>
      api.PUT('/api/categories/{id}', { params: { path: { id } }, body }),
    remove: (id: string) => api.DELETE('/api/categories/{id}', { params: { path: { id } } }),
    move: (id: string, parent_id: string | null) =>
      api.POST('/api/categories/{id}/move', { params: { path: { id } }, body: { parent_id } }),
  },
  footprints: {
    tree: async () => (await api.GET('/api/footprints/tree')).data ?? [],
    get: async (id: string) =>
      (await api.GET('/api/footprints/{id}', { params: { path: { id } } })).data,
    create: (body: components['schemas']['FootprintInput']) =>
      api.POST('/api/footprints', { body }),
    update: (id: string, body: components['schemas']['FootprintInput']) =>
      api.PUT('/api/footprints/{id}', { params: { path: { id } }, body }),
    remove: (id: string) => api.DELETE('/api/footprints/{id}', { params: { path: { id } } }),
    move: (id: string, parent_id: string | null) =>
      api.POST('/api/footprints/{id}/move', { params: { path: { id } }, body: { parent_id } }),
  },
  locations: {
    tree: async () => (await api.GET('/api/locations/tree')).data ?? [],
    get: async (id: string) =>
      (await api.GET('/api/locations/{id}', { params: { path: { id } } })).data,
    create: (body: components['schemas']['StorageLocationInput']) =>
      api.POST('/api/locations', { body }),
    update: (id: string, body: components['schemas']['StorageLocationInput']) =>
      api.PUT('/api/locations/{id}', { params: { path: { id } }, body }),
    remove: (id: string) => api.DELETE('/api/locations/{id}', { params: { path: { id } } }),
    move: (id: string, parent_id: string | null) =>
      api.POST('/api/locations/{id}/move', { params: { path: { id } }, body: { parent_id } }),
  },
  'attachment-types': {
    tree: async () => (await api.GET('/api/attachment-types/tree')).data ?? [],
    get: async (id: string) =>
      (await api.GET('/api/attachment-types/{id}', { params: { path: { id } } })).data,
    create: (body: components['schemas']['AttachmentTypeInput']) =>
      api.POST('/api/attachment-types', { body }),
    update: (id: string, body: components['schemas']['AttachmentTypeInput']) =>
      api.PUT('/api/attachment-types/{id}', { params: { path: { id } }, body }),
    remove: (id: string) => api.DELETE('/api/attachment-types/{id}', { params: { path: { id } } }),
    move: (id: string, parent_id: string | null) =>
      api.POST('/api/attachment-types/{id}/move', {
        params: { path: { id } },
        body: { parent_id },
      }),
  },
}

/** A tree node flattened into a row that knows where it sits. */
export interface FlatNode {
  id: string
  name: string
  depth: number
  /** Full path, root first, e.g. "Passive / Resistors / SMD". */
  path: string
  directCount: number
  totalCount: number
  hasChildren: boolean
  parentId: string | null
  previewAttachmentId: string | null
}

export function flatten(
  nodes: TreeNode[],
  depth = 0,
  prefix = '',
  parentId: string | null = null,
): FlatNode[] {
  const out: FlatNode[] = []
  for (const node of nodes) {
    const path = prefix ? `${prefix} / ${node.name}` : node.name
    out.push({
      id: node.id,
      name: node.name,
      depth,
      path,
      directCount: node.direct_count,
      totalCount: node.total_count,
      hasChildren: node.children.length > 0,
      parentId,
      previewAttachmentId: node.preview_attachment_id ?? null,
    })
    out.push(...flatten(node.children, depth + 1, path, node.id))
  }
  return out
}

/**
 * Everything at or below `id`. A picker uses this to grey out the options that
 * would file a node inside its own branch — the backend refuses those, but the
 * user should not be offered them in the first place.
 */
export function subtreeIds(nodes: FlatNode[], id: string): Set<string> {
  const out = new Set<string>([id])
  let changed = true
  while (changed) {
    changed = false
    for (const node of nodes) {
      if (node.parentId && out.has(node.parentId) && !out.has(node.id)) {
        out.add(node.id)
        changed = true
      }
    }
  }
  return out
}
